import { useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useFocusMode } from './useFocusMode';
import { useFocusAnimation } from './useFocusAnimation';
import { findTaskElement } from '../cardBitmaps/findTaskElement';

/**
 * Hook that integrates focus mode with the flowchart
 * 
 * Combines useFocusMode and useFocusAnimation, and handles:
 * - Focus mode state management
 * - Focus animations
 * - External focusMission events
 * - Ref synchronization
 */
export function useFocusModeIntegration({
  tasks,
  nodePositions,
  nodeDimensions,
  edges,
  containerRef,
  pan,
  zoom,
  setPanUnconstrained,
  setZoom,
  onFocusModeChange,
  disableDragRef,
  isFocusModeRef,
  setSelectedNodes,
  isMultiCampaignMode = false,
  statuses = [],
  initialFocusedTaskIds = null, // For restoring FOKUS state after tab switch
}) {
  // Track if we're doing an instant transition (skip animations)
  const instantTransitionRef = useRef(false);
  // Track if we need to do a deferred instant positioning (when positions aren't ready yet)
  const pendingInstantPositionRef = useRef(false);
  // Core focus mode hook
  const focusMode = useFocusMode({
    tasks,
    nodePositions,
    nodeDimensions,
    edges,
    containerRef,
    pan,
    zoom,
    setPan: setPanUnconstrained,
    setZoom,
    onFocusModeChange,
    isMultiCampaignMode,
    statuses,
    initialFocusedTaskIds,
  });

  const {
    focusedTaskIds,
    allFocusedTaskIds,
    focusedTaskNames,
    hiddenConnections,
    displayTasks,
    displayPositions,
    displayEdges,
    focusContentBounds,
    handleFocusTask,
    handleFocusTasks,
    handleExitFocusMode,
    addToFocusedTasks,
    isFocusMode,
    centerOnTask,
    resetViewForFocusMode,
    resetViewForFokusModeInstant,
    panBackToNodes,
    panToFocusLayout,
    zoomToFocusLayout,
    zoomOutForExit,
    lastFocusPositions,
    lastFocusEdges,
    lastFocusedTaskIds,
    // Instant handlers (no animation - for view switching)
    handleFocusTasksInstant,
    handleExitFocusModeInstant,
  } = focusMode;

  // Sync focus mode state to refs
  useEffect(() => {
    disableDragRef.current = isFocusMode;
    isFocusModeRef.current = isFocusMode;
  }, [isFocusMode, disableDragRef, isFocusModeRef]);

  // Empty handler for pan to nodes (placeholder)
  const handlePanToNodes = useCallback(() => {}, []);

  // Get effective focused IDs for animation
  const effectiveFocusedIds = allFocusedTaskIds || lastFocusedTaskIds;

  // Focus animation hook
  const focusAnimation = useFocusAnimation({
    isFocusMode,
    allFocusedTaskIds: effectiveFocusedIds,
    nodePositions,
    displayPositions,
    tasks,
    edges,
    displayEdges,
    lastFocusPositions,
    lastFocusEdges,
    onStartPanToNodes: handlePanToNodes,
    onStartPanToFocusLayout: panToFocusLayout,
    onStartZoomToFocusLayout: zoomToFocusLayout,
    onStartPanToFocusLayoutInstant: resetViewForFokusModeInstant,
    onStartZoomOutForExit: zoomOutForExit,
    onStartPanBack: panBackToNodes,
    instantTransitionRef, // Pass ref to skip animations when switching views
  });

  const {
    isAnimatingIntoFocus,
    isAnimatingOutOfFocus,
    positionsToUse,
    edgesToRender,
    tasksToRender: animationTasksToRender,
    getNodeAnimationState,
  } = focusAnimation;

  const tasksToRender = animationTasksToRender || displayTasks || [];

  // Handle external focusMission event for Flow view (center + select + flash)
  useEffect(() => {
    const handleFocusMission = (event) => {
      const { missionId, view } = event.detail || {};
      if (view !== 'flow' || !missionId) return;
      
      // Force exit focus mode if active
      if (isFocusModeRef.current) {
        handleExitFocusMode();
      }
      
      // Select and center (same as toolbar Center), but never enter focus mode
      setSelectedNodes(new Set([missionId]));
      setTimeout(() => {
        centerOnTask(missionId, { adjustZoom: true });
        
        // Flash highlight the node AFTER the pan/zoom animation completes
        // The centerOnTask animation takes ~400ms (see useViewportControls)
        setTimeout(() => {
          const nodeElement = findTaskElement(missionId);
          if (nodeElement) {
            nodeElement.classList.add('task-node--highlighted');
            setTimeout(() => {
              nodeElement.classList.remove('task-node--highlighted');
            }, 1000); // 2 fades at 0.4s each = 0.8s, plus buffer
          }
        }, 450); // Wait for pan/zoom animation to complete
      }, 0);
    };
    
    window.addEventListener('focusMission', handleFocusMission);
    return () => window.removeEventListener('focusMission', handleFocusMission);
  }, [centerOnTask, setSelectedNodes, handleExitFocusMode, isFocusModeRef]);

  // Wrapped exit handler that ensures instant flag is cleared
  // This is needed because after entering FOKUS via view switch (instant),
  // if the user manually clicks "Exit FOKUS", we want the animated exit
  const handleExitFocusModeAnimated = useCallback(() => {
    // Clear instant flag to ensure we get the animated exit
    instantTransitionRef.current = false;
    handleExitFocusMode();
  }, [handleExitFocusMode]);

  // Instant focus mode handlers (for view switching - no animation)
  const focusTasksInstant = useCallback((taskIds) => {
    instantTransitionRef.current = true;
    pendingInstantPositionRef.current = true; // Mark that we need to position after positions are ready
    handleFocusTasksInstant(taskIds);
    // Note: instantTransitionRef is cleared by useFocusAnimation after it processes the change
  }, [handleFocusTasksInstant]);
  
  const exitFocusModeInstant = useCallback(() => {
    instantTransitionRef.current = true;
    handleExitFocusModeInstant();
    // Note: instantTransitionRef is cleared by useFocusAnimation after it processes the change
  }, [handleExitFocusModeInstant]);
  
  // Handle deferred instant positioning - when positions become available after instant focus
  // This is needed because resetViewForFokusModeInstant depends on focusAutoPositions,
  // which isn't calculated until after the focus state change propagates through useMemo
  useLayoutEffect(() => {
    if (pendingInstantPositionRef.current && isFocusMode && displayPositions && Object.keys(displayPositions).length > 0) {
      // Positions are now ready, do the instant positioning
      resetViewForFokusModeInstant();
      pendingInstantPositionRef.current = false;
    }
  }, [isFocusMode, displayPositions, resetViewForFokusModeInstant]);

  return {
    // Focus mode state
    focusedTaskIds,
    allFocusedTaskIds,
    focusedTaskNames,
    hiddenConnections,
    displayTasks,
    displayPositions,
    displayEdges,
    focusContentBounds,
    isFocusMode,
    
    // Focus mode actions
    handleFocusTask,
    handleFocusTasks,
    handleExitFocusMode: handleExitFocusModeAnimated,
    addToFocusedTasks,
    centerOnTask,
    
    // Instant focus mode actions (for view switching - no animation)
    focusTasksInstant,
    exitFocusModeInstant,
    
    // Animation state
    isAnimatingIntoFocus,
    isAnimatingOutOfFocus,
    positionsToUse,
    edgesToRender,
    tasksToRender,
    getNodeAnimationState,
  };
}

export default useFocusModeIntegration;

